
import { useState, useEffect } from 'react';
import { useAuthContext } from '@/contexts/auth';
import { supabase } from '@/lib/supabase';
import { debugLog, debugError } from '@/utils/debugLogger';
import type { Dealer } from '@/types/dealer';

/**
 * Hook that checks if the authenticated user has any activated dealer accounts
 * Returns the auth context with additional dealer status properties
 */
export const useDealerStatus = () => {
  const auth = useAuthContext();
  const { user, isAuthenticated } = auth;
  
  const [hasActivatedDealers, setHasActivatedDealers] = useState(false);
  const [activatedDealers, setActivatedDealers] = useState<Dealer[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkDealerStatus = async () => {
      try {
        if (!isAuthenticated || !user?.id) {
          setHasActivatedDealers(false);
          setActivatedDealers([]);
          setIsLoading(false);
          return;
        }
        
        debugLog('dealer', "Checking dealer status for user:", user.id, 'useDealerStatus');
        
        // Get all activated and active dealer entries for the user
        const { data, error } = await supabase
          .from('dealers')
          .select('id, name, city, address, activated_at, oam_code, oam_verified, opo_code, opo_status, price_lock_enabled, price_lock_duration_hours, activated, active, opening_hours, phone, email, website, postal_code, image_url')
          .eq('user_id', user.id)
          .eq('activated', true)
          .eq('active', true);
          
        if (error) {
          debugError('dealer', "Error checking dealer status:", error, 'useDealerStatus');
          setHasActivatedDealers(false);
          setActivatedDealers([]);
        } else {
          const dealers: Dealer[] = (data || []).map(d => ({
            rating: 0,
            is_verified: false,
            isFavorite: false,
            ...d,
          }));
          const hasDealers = dealers.length > 0;
          debugLog('dealer', "User has activated dealers:", { hasDealers, count: dealers.length }, 'useDealerStatus');
          setHasActivatedDealers(hasDealers);
          setActivatedDealers(dealers);
        }
      } catch (error) {
        debugError('dealer', "Exception checking dealer status:", error, 'useDealerStatus');
        setHasActivatedDealers(false);
        setActivatedDealers([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkDealerStatus();
  }, [isAuthenticated, user]);

  return {
    ...auth,
    hasActivatedDealers,
    activatedDealers,
    isDealerLoading: isLoading
  };
};

export default useDealerStatus;
